/**
 * Turn the org's configured member-profile layout into print sections
 * (TRUS-636). Mirrors what the Member Profile shows: the same grouping and
 * order (buildConfiguredGroups), core values read the same way as
 * MemberProfileNew.coreFieldValue, custom values from member.custom_fields, and
 * conditional fields hidden when their condition is not met.
 */

import type { Member, ProfileField } from '@/services/api/members';
import { buildConfiguredGroups } from '../memberFormCategories';
import { isFieldVisible } from '@/components/forms/fieldVisibility';
import type { PacketProfileSection } from './printPacketBuilder';

type Translate = (key: string, fallback: string) => string;

/** Core field keys whose member column has a different name. */
export function coreMemberValue(member: Record<string, any> | null | undefined, key: string): unknown {
  if (!member) return undefined;
  switch (key) {
    case 'phone_no':
      return member.phone_number ?? member.phone_no;
    case 'date_of_birth':
      return member.date_of_birth ?? member.dob;
    case 'preferred_language':
      // TRUS-738: one column, no fallback to `language`.
      return member.preferred_language;
    default:
      return member[key];
  }
}

/**
 * For orgs without a Profile Builder layout: the core fields the legacy
 * profile shows, so "Member profile" still prints something useful.
 */
export function fallbackProfileFields(tr: Translate): ProfileField[] {
  const mk = (category_key: string, category_name: string, category_sort: number) =>
    (field_key: string, display_name: string, field_type = 'str', field_sort = 0): ProfileField => ({
      field_key,
      display_name,
      field_type,
      category_key,
      category_name,
      category_sort,
      field_sort,
      is_core_field: true,
      is_hidden: false,
      is_required: false,
      is_readonly: false,
      display_width: 'half',
    });
  const id = mk('identity', tr('printPacket.fallback.identity', 'Identity'), 1);
  const contact = mk('contact', tr('printPacket.fallback.contact', 'Contact'), 2);
  const coverage = mk('coverage', tr('printPacket.fallback.coverage', 'Coverage'), 3);
  return [
    id('first_name', tr('printPacket.fallback.firstName', 'First name'), 'str', 1),
    id('last_name', tr('printPacket.fallback.lastName', 'Last name'), 'str', 2),
    id('date_of_birth', tr('printPacket.fallback.dateOfBirth', 'Date of birth'), 'date', 3),
    id('gender', tr('printPacket.fallback.gender', 'Gender'), 'str', 4),
    id('preferred_language', tr('printPacket.fallback.language', 'Preferred language'), 'str', 5),
    contact('phone_no', tr('printPacket.fallback.phone', 'Phone'), 'str', 1),
    contact('email', tr('printPacket.fallback.email', 'Email'), 'str', 2),
    contact('street_address', tr('printPacket.fallback.address', 'Street address'), 'str', 3),
    contact('city', tr('printPacket.fallback.city', 'City'), 'str', 4),
    contact('state', tr('printPacket.fallback.state', 'State'), 'str', 5),
    contact('zip_code', tr('printPacket.fallback.zip', 'ZIP code'), 'str', 6),
    coverage('medicaid_id', tr('printPacket.fallback.medicaidId', 'Medicaid ID'), 'str', 1),
  ];
}

/**
 * @param member  null for a blank packet — no values, and every conditional
 *                field prints (the condition can't be judged on paper yet).
 */
export function buildProfilePrintSections(
  profileFields: ProfileField[],
  member: Member | null,
): PacketProfileSection[] {
  const custom: Record<string, any> = (member?.custom_fields as Record<string, any>) || {};

  return buildConfiguredGroups(profileFields)
    .map((group) => ({
      key: group.key,
      title: group.name,
      fields: group.items
        .filter(
          (pf) =>
            !member ||
            pf.is_core_field ||
            !pf.visibility_rules ||
            isFieldVisible({ field_id: pf.field_key, visibility_rules: pf.visibility_rules as any }, [], custom),
        )
        .map((pf) => ({
          key: pf.field_key,
          label: pf.display_name,
          type: pf.field_type,
          options: pf.field_options ?? null,
          required: pf.is_required,
          value: member
            ? pf.is_core_field
              ? coreMemberValue(member as Record<string, any>, pf.field_key)
              : custom[pf.field_key]
            : undefined,
        })),
    }))
    .filter((s) => s.fields.length > 0);
}
